import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Crown, Trophy, Zap, Sparkles, Loader2, KeyRound, Shield, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";
import { getBillingPlans, redeemCode, getMySubscription, switchToYearly, activatePaypalSubscription, createStripeCheckout } from "@/lib/api";

const PLAN_META = {
  standard: {
    Icon: Zap,
    tagline: "For everyday work",
    model: "GPT-5.4 · Claude Sonnet 4.6 · Gemini 3.1 Pro",
    features: [
      "GPT-5.4, GPT-4o & GPT-5.4 Mini",
      "Claude Sonnet 4.6 & Haiku 4.5",
      "Gemini 3.1 Pro & 3.5 Flash",
      "Unlimited conversations & history",
    ],
  },
  pro: {
    Icon: Crown,
    tagline: "Top-tier reasoning",
    model: "GPT-5.5 · Claude Sonnet 5 · Opus 4.7",
    features: [
      "Everything in Standard",
      "GPT-5.5 advanced reasoning",
      "Claude Sonnet 5 & Opus 4.7",
      "Gemini 3.8 Flash",
      "File & image attachments",
    ],
  },
  pro_max: {
    Icon: Trophy,
    tagline: "Every frontier model",
    model: "ChatGPT Astra · Claude Opus 5 · GPT-5.6",
    features: [
      "Everything in Pro",
      "ChatGPT Astra (GPT-6) & GPT-5.6 Sol/Terra/Luna",
      "Claude Opus 5 & Opus 4.8",
      "Racing mode — GPT vs Claude vs Gemini",
      "All 19 models, switch any time",
    ],
    highlighted: true,
  },
};

const ORDER = ["standard", "pro", "pro_max"];

export default function Upgrade() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [billingData, setBillingData] = useState(null);
  const [cadence, setCadence] = useState("monthly"); // "monthly" | "yearly"
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [openPlan, setOpenPlan] = useState(null);
  const [mySub, setMySub] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null); // lookup_key in flight

  const startCheckout = async (planCode, kind) => {
    const lookupKey = `${planCode}_${kind}`;
    setCheckingOut(lookupKey);
    try {
      const res = await createStripeCheckout(lookupKey);
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setCheckingOut(null);
    }
  };
  // Track which subscription_ids we've already attempted to activate so React
  // double-renders + state changes don't cause repeat /activate calls.
  const activatedRef = useRef(new Set());

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    getBillingPlans()
      .then((r) => setBillingData(r.data))
      .catch(() => toast.error("Could not load pricing"));
    getMySubscription()
      .then((r) => setMySub(r.data?.subscription || null))
      .catch(() => {});
  }, [user, navigate]);

  // Handle return from PayPal approval (fallback link flow):
  // PayPal redirects back to /upgrade?subscription_id=I-... after approval.
  // We auto-call /billing/subscription/activate so the user doesn't have to
  // do anything extra. Fire EXACTLY ONCE per subscription_id (the useRef
  // sentinel survives re-renders).
  useEffect(() => {
    if (!user) return;
    const subId = searchParams.get("subscription_id");
    if (!subId) return;
    if (activatedRef.current.has(subId)) return;
    activatedRef.current.add(subId);

    activatePaypalSubscription(subId)
      .then(async () => {
        toast.success("Subscription active — enjoy your new tier!");
        await refreshUser();
      })
      .catch((e) => {
        const msg = e.response?.data?.detail || "Could not activate subscription";
        toast.error(msg);
      })
      .finally(() => {
        // Always sanitise the URL — on both success and failure — so a refresh
        // doesn't re-trigger the activation attempt and keep toasting.
        const next = new URLSearchParams(searchParams);
        next.delete("subscription_id");
        next.delete("ba_token");
        next.delete("token");
        next.delete("sub");
        setSearchParams(next, { replace: true });
      });
  }, [user, searchParams, refreshUser, setSearchParams]);

  const handleRedeem = async (e) => {
    e?.preventDefault();
    if (!code.trim()) {
      toast.error("Enter a code");
      return;
    }
    setRedeeming(true);
    try {
      const res = await redeemCode(code.trim().toUpperCase());
      toast.success(res.data.message);
      setCode("");
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to redeem");
    } finally {
      setRedeeming(false);
    }
  };

  const planMap = useMemo(() => {
    const m = {};
    (billingData?.plans || []).forEach((p) => { m[p.code] = p; });
    return m;
  }, [billingData]);

  const currentPlan = user?.plan || "free";
  const expiresAt = user?.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString() : null;
  const clientId = billingData?.paypal_client_id;

  // Monthly subscriber → eligible for yearly switch
  const monthlySubPlan = mySub && mySub.cadence === "monthly" ? mySub.plan : null;
  const yearlySavings = useMemo(() => {
    if (!monthlySubPlan || !planMap[monthlySubPlan]) return 0;
    const p = planMap[monthlySubPlan];
    return Math.round(p.price_usd * 12 - p.yearly_usd);
  }, [monthlySubPlan, planMap]);

  const handleSwitchToYearly = async () => {
    setSwitching(true);
    try {
      const res = await switchToYearly();
      if (res.data?.approval_url) {
        toast.message("Redirecting to PayPal to approve the yearly plan…");
        window.location.href = res.data.approval_url;
      } else {
        toast.error("Could not start the switch — please try again");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not switch to yearly");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/chat")} data-testid="back-to-chat-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Logo />
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 lg:px-8 lg:py-16">
        {/* Current plan + redeem */}
        <div className="mb-10 grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card/50 p-6" data-testid="current-tier-card">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Current tier</div>
            <div className="mt-2 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/15 text-primary">
                {currentPlan === "free" ? <Sparkles className="h-5 w-5" /> :
                 currentPlan === "pro_max" ? <Trophy className="h-5 w-5" /> :
                 currentPlan === "pro" ? <Crown className="h-5 w-5" /> :
                 <Zap className="h-5 w-5" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-xl font-semibold tracking-tight" data-testid="current-plan-label">
                    {currentPlan === "free" ? "Free" :
                     currentPlan === "pro_max" ? "Pro Max" :
                     currentPlan === "pro" ? "Pro" : "Standard"}
                  </div>
                  {mySub?.cadence && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" data-testid="current-cadence-badge">
                      {mySub.cadence}
                    </span>
                  )}
                </div>
                {expiresAt && (
                  <div className="text-xs text-muted-foreground" data-testid="current-plan-expiry">Expires {expiresAt}</div>
                )}
              </div>
            </div>

            {/* Switch to yearly CTA (active monthly subscribers only) */}
            {monthlySubPlan && yearlySavings > 0 && (
              <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="switch-to-yearly-cta">
                <div className="flex items-center gap-2.5">
                  <div className="grid h-7 w-7 place-items-center rounded-lg bg-emerald-500/15 text-emerald-500">
                    <TrendingUp className="h-3.5 w-3.5" />
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-foreground">Save ${yearlySavings}/yr</div>
                    <div className="text-[11px] text-muted-foreground">Switch to yearly billing — 2 months free</div>
                  </div>
                </div>
                <Button
                  onClick={handleSwitchToYearly}
                  disabled={switching}
                  size="sm"
                  className="h-8 rounded-lg bg-emerald-500 px-3 text-xs font-semibold text-white hover:bg-emerald-600"
                  data-testid="switch-to-yearly-btn"
                >
                  {switching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Switch"}
                </Button>
              </div>
            )}
          </div>

          <form onSubmit={handleRedeem} className="rounded-2xl border border-primary/30 bg-card p-6" data-testid="redeem-form">
            <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-widest text-primary">
              <KeyRound className="h-3.5 w-3.5" /> Have a promo code?
            </div>
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="SABZYAI…"
                className="h-11 rounded-lg uppercase tracking-widest"
                maxLength={32}
                data-testid="promo-code-input"
              />
              <Button type="submit" disabled={redeeming} className="h-11 rounded-lg px-5" data-testid="redeem-submit-btn">
                {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
              </Button>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Unlock a tier instantly with a code — bypasses checkout.
            </div>
          </form>
        </div>

        {/* Plans header + billing-mode toggle */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-primary">Pricing</div>
            <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Pick a plan that fits</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Cancel anytime. Secure checkout by Stripe — your card details never touch our servers.
            </p>
          </div>
          <div className="inline-flex rounded-full border border-border bg-card p-1 text-xs font-semibold" data-testid="billing-cadence-toggle">
            <button
              className={`rounded-full px-4 py-2 transition ${cadence === "monthly" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground"}`}
              onClick={() => { setCadence("monthly"); setOpenPlan(null); }}
              data-testid="cadence-monthly"
              type="button"
            >
              Monthly
            </button>
            <button
              className={`rounded-full px-4 py-2 transition ${cadence === "pass" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground"}`}
              onClick={() => { setCadence("pass"); setOpenPlan(null); }}
              data-testid="cadence-pass"
              type="button"
            >
              30-day pass
            </button>
            <button
              className={`relative rounded-full px-4 py-2 transition ${cadence === "yearly" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground"}`}
              onClick={() => { setCadence("yearly"); setOpenPlan(null); }}
              data-testid="cadence-yearly"
              type="button"
            >
              Yearly
              <span className="ml-1.5 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-500">−17%</span>
            </button>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-3" data-testid="plans-grid">
          {ORDER.map((code) => {
            const plan = planMap[code];
            const meta = PLAN_META[code];
            if (!plan || !meta) return null;
            const { Icon } = meta;
            const isCurrent = currentPlan === code;
            const isHighlighted = meta.highlighted;
            const isOpen = openPlan === code;
            return (
              <div
                key={code}
                className={`relative flex flex-col rounded-2xl border p-6 transition ${
                  isHighlighted
                    ? "border-primary/60 bg-card shadow-xl shadow-primary/10"
                    : "border-border bg-card/50"
                } ${isCurrent ? "ring-2 ring-primary" : ""}`}
                data-testid={`upgrade-plan-${code}`}
              >
                {isCurrent && (
                  <div className="absolute -top-3 right-4 rounded-full bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-primary-foreground">
                    Active
                  </div>
                )}
                {isHighlighted && !isCurrent && (
                  <div className="absolute -top-3 right-4 rounded-full bg-amber-500 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
                    Best value
                  </div>
                )}
                <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{meta.tagline}</div>
                <div className="text-xl font-bold tracking-tight">{plan.name}</div>
                <div className="mt-1 text-xs text-muted-foreground">{meta.model}</div>

                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold tracking-tight" data-testid={`price-${code}`}>
                    ${cadence === "yearly" ? plan.yearly_usd.toFixed(0) : plan.price_usd.toFixed(0)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {cadence === "yearly" ? " / year" : cadence === "pass" ? " one-time · 30 days" : " / month"}
                  </span>
                </div>
                {cadence === "yearly" && (
                  <div className="mt-1 text-[11px] text-emerald-500" data-testid={`yearly-savings-${code}`}>
                    Save ${(plan.price_usd * 12 - plan.yearly_usd).toFixed(0)}/yr · 2 months free
                  </div>
                )}

                <div className="my-5 border-t border-border" />

                <ul className="flex-1 space-y-2.5 text-sm">
                  {meta.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  <Button
                    onClick={() => startCheckout(code, cadence)}
                    disabled={isCurrent || !!checkingOut}
                    className="h-11 w-full rounded-xl"
                    data-testid={`choose-plan-${code}`}
                  >
                    {checkingOut === `${code}_${cadence}` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isCurrent ? "Current plan"
                      : cadence === "yearly" ? `Subscribe — $${plan.yearly_usd.toFixed(0)}/yr`
                      : cadence === "pass" ? `Buy 30-day pass — $${plan.price_usd.toFixed(0)}`
                      : `Subscribe — $${plan.price_usd.toFixed(0)}/mo`}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground" data-testid="trust-strip">
          <Shield className="h-3.5 w-3.5" />
          <span>Secure checkout by Stripe · Cancel anytime · USD billing · No card stored on Sabzy</span>
        </div>
      </main>
    </div>
  );
}
